import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5076/api';

// Export API_URL for constructing image URLs
export { API_URL };

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token to requests
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Request queue and rate limiting
let requestQueue = [];
let isProcessingQueue = false;
const RATE_LIMIT_DELAY = 100; // ms between requests

// Rate limiting function
const processQueue = async () => {
  if (isProcessingQueue || requestQueue.length === 0) return;

  isProcessingQueue = true;
  while (requestQueue.length > 0) {
    const { resolve, reject, request } = requestQueue.shift();
    try {
      const response = await request();
      resolve(response);
    } catch (error) {
      reject(error);
    }
    // Wait before next request
    await new Promise(resolve => setTimeout(resolve, RATE_LIMIT_DELAY));
  }
  isProcessingQueue = false;
};

// Retry function with exponential backoff
const retryRequest = async (requestFn, maxRetries = 3) => {
  let lastError;

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await requestFn();
    } catch (error) {
      lastError = error;

      if (error.response?.status === 429) {
        // Rate limited - wait longer
        const backoffDelay = Math.pow(2, attempt) * 1000 + Math.random() * 1000;
        await new Promise(resolve => setTimeout(resolve, backoffDelay));
        continue;
      }

      if (error.response?.status >= 500 || attempt === maxRetries) {
        throw error;
      }

      // For other errors, retry with exponential backoff
      const backoffDelay = Math.pow(2, attempt) * 500;
      await new Promise(resolve => setTimeout(resolve, backoffDelay));
    }
  }

  throw lastError;
};

// Handle response errors
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response?.status === 401) {
      const currentPath = window.location.pathname;
      
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      
      // Only redirect if not already on login/register page
      if (currentPath !== '/login' && currentPath !== '/register') {
        window.location.href = '/login';
      }
      return Promise.reject(error);
    }

    // Don't retry for auth errors or client errors
    if (error.response?.status === 400 || error.response?.status === 403) {
      return Promise.reject(error);
    }

    // For rate limiting and server errors, add to queue for retry
    if (error.response?.status === 429 || error.response?.status >= 500) {
      return new Promise((resolve, reject) => {
        requestQueue.push({
          resolve,
          reject,
          request: () => retryRequest(() => api.request(error.config))
        });
        processQueue();
      });
    }

    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: (data) => api.post('/auth/login', data),
  register: (data) => api.post('/auth/register', data),
  getProfile: () => api.get('/auth/me'),
  changePassword: (data) => api.post('/auth/change-password', data),
  logout: () => api.post('/auth/logout'),
};

// Employees API
export const employeesAPI = {
  getAll: (params) => api.get('/employees', { params }),
  getById: (id) => api.get(`/employees/${id}`),
  register: (data) => api.post('/employees', data),
  update: (id, data) => api.put(`/employees/${id}`, data),
  delete: (id) => api.delete(`/employees/${id}`),
  updateLeaveBalance: (id, data) => api.put(`/employees/${id}/leave-balance`, data),
  getDepartments: () => api.get('/employees/departments'),
};

// Face Verification API
export const faceAPI = {
  register: (data) => api.post('/face/register', data, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  verify: (data) => api.post('/face/verify', data),
};

// Attendance API
export const attendanceAPI = {
  login: (data) => api.post('/attendance/login', data, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  checkInFace: (data) => api.post('/attendance/checkin-face', data, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  logout: (data) => api.post('/attendance/logout', data, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  checkOutFace: (data) => api.post('/attendance/checkout-face', data, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  getToday: () => api.get('/attendance/today'),
  getHistory: (params) => api.get('/attendance/history', { params }),
  getStats: (employeeIdOrParams, month, year) => {
    if (typeof employeeIdOrParams === 'object' && employeeIdOrParams !== null) {
      return api.get('/dashboard/employee-stats', { params: employeeIdOrParams });
    }
    return api.get(`/dashboard/employee/${employeeIdOrParams}/summary`, { params: { month, year } });
  },
  startBreak: (data) => api.post('/attendance/break/start', data, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  breakInFace: (data) => api.post('/attendance/breakin-face', data, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  endBreak: (data) => api.post('/attendance/break/end', data, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  breakOutFace: (data) => api.post('/attendance/breakout-face', data, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  getBreakStatus: () => api.get('/attendance/break-status'),
  updateWorkType: (data) => api.post('/attendance/work-type', data),
  createBreakRequest: (data) => api.post('/attendance/break-request', data),
  getBreakRequests: (params) => api.get('/attendance/break-requests', { params }),
  updateStatus: (id, status, note) => api.put(`/attendance/${id}/status`, { status, note }),
  create: (data) => api.post('/attendance', data),
  bulkMarkPresent: (data) => api.post('/attendance/bulk-mark-present', data),
  approveBreakRequest: (id) => api.put(`/attendance/break-request/${id}/approve`),
  rejectBreakRequest: (id, data) => api.put(`/attendance/break-request/${id}/reject`, data),
  markStatusWithTimes: (data) => api.post('/attendance/mark-status-with-times', data),
  monthly: (year, month) => api.get(`/attendance/monthly/${year}/${month}`),
  getEmployeeMonthlyStats: (employeeId, month, year) => {
    const id = typeof employeeId === 'object' && employeeId !== null ? employeeId.id : employeeId;
    return api.get(`/dashboard/employee/${id}/summary`, { params: { month, year } });
  },
};

// Leave API
export const leaveAPI = {
  apply: (data) => api.post('/leave/apply', data),
  createForEmployee: (data) => api.post('/leave/create-for-employee', data),
  getMyLeaves: (params) => api.get('/leave/my-leaves', { params }),
  getBalance: () => api.get('/leave/balance'),
  getAll: (params) => api.get('/leave/all', { params }),
  getAllAdmin: () => api.get('/leave/all'),
  approve: (id) => api.put(`/leave/${id}/approve`),
  reject: (id, data) => api.put(`/leave/${id}/reject`, data),
  hold: (id) => api.put(`/leave/${id}/hold`),
  releaseHold: (id) => api.put(`/leave/${id}/release-hold`),
  put: (id, data) => api.put(`/leave/${id}`, data),
  cancel: (id, reason) => api.delete(`/leave/${id}`, { data: { reason } }),
  approveCancellation: (id) => api.put(`/leave/${id}/approve-cancellation`),
  rejectCancellation: (id) => api.put(`/leave/${id}/reject-cancellation`),
  getPendingCount: () => api.get('/leave/pending-count'),
};

// Salary Details API
export const salaryDetailsAPI = {
  getByEmployee: (employeeId) => api.get(`/salary-details/${employeeId}`),
  update: (employeeId, data) => api.put(`/salary-details/${employeeId}`, data),
  getAll: (params) => api.get('/salary-details', { params }),
};

// Shifts API
export const shiftsAPI = {
  getAll: () => api.get('/shifts'),
  getAllAdmin: (params) => api.get('/shifts/all', { params }),
  getById: (id) => api.get(`/shifts/${id}`),
  create: (data) => api.post('/shifts', data),
  update: (id, data) => api.put(`/shifts/${id}`, data),
  delete: (id) => api.delete(`/shifts/${id}`),
  assign: (id, data) => api.post(`/shifts/${id}/assign`, data),
};

// Holidays API
export const holidaysAPI = {
  getAll: (params) => api.get('/holidays', { params }),
  getById: (id) => api.get(`/holidays/${id}`),
  create: (data) => api.post('/holidays', data),
  update: (id, data) => api.put(`/holidays/${id}`, data),
  delete: (id) => api.delete(`/holidays/${id}`),
  getUpcoming: () => api.get('/holidays/upcoming/list'),
  getAllSelections: (params) => api.get('/holidays/selections/all', { params }),
  approveSelection: (id) => api.put(`/holidays/selections/${id}/approve`),
  rejectSelection: (id, data) => api.put(`/holidays/selections/${id}/reject`, data),
  bulkUpdateSelections: (data) => api.post('/holidays/selections/bulk-update', data),
  getDepartments: () => api.get('/holidays/departments/list'),
  getStats: (params) => api.get('/holidays/stats/dashboard', { params }),
  // Category-based holiday selection
  getCategories: (year) => api.get('/holidays/categories', { params: { year } }),
  submitCategorySelections: (selections) => api.post('/holidays/categories/select', { selections }),
  getMyCategorySelections: (year) => api.get('/holidays/categories/my-selections', { params: { year } }),
};

// Notifications API
export const notificationsAPI = {
  getAll: (params) => api.get('/notifications', { params }),
  getUnreadCount: () => api.get('/notifications/unread-count'),
  markRead: (id) => api.put(`/notifications/${id}/read`),
  markAllRead: () => api.put('/notifications/read-all'),
  send: (data) => api.post('/notifications', data),
  delete: (id) => api.delete(`/notifications/${id}`),
};

// Dashboard API
export const dashboardAPI = {
  getStats: () => api.get('/dashboard/stats'),
  getRealtime: () => api.get('/dashboard/realtime'),
  getEmployeeStats: (params) => api.get('/dashboard/employee-stats', { params }),
  getCalendar: (params) => api.get('/dashboard/calendar', { params }),
  getEmployeeSummary: (id, params) => api.get(`/dashboard/employee/${id}/summary`, { params }),
  getWorkingDays: (params) => api.get('/dashboard/working-days', { params }),
};

// Settings API
export const settingsAPI = {
  getAll: () => api.get('/settings'),
  getByCategory: (category) => api.get(`/settings/category/${category}`),
  getByKey: (key) => api.get(`/settings/${key}`),
  save: (data) => api.put('/settings', data),
  bulkSave: (settings) => api.post('/settings/bulk', { settings }),
  delete: (key) => api.delete(`/settings/${key}`),
  deleteByKey: (key) => api.delete(`/settings/${key}`),
  getAlertConfig: () => api.get('/settings/alerts/config'),
  saveAlertConfig: (alerts, type) => api.post('/settings/alerts/config', { alerts, type }),
  sendTestEmail: (data) => api.post('/settings/alerts/test-email', data),
  uploadLogo: (formData) => api.post('/settings/logo/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  deleteLogo: () => api.delete('/settings/logo'),
  // Google Sheets Integration
  getGoogleSheetsConfig: () => api.get('/settings/google-sheets/config'),
  saveGoogleSheetsConfig: (config) => api.post('/settings/google-sheets/config', config),
  syncToGoogleSheets: (type) => api.post('/settings/google-sheets/sync', { type }),
  getSyncStatus: () => api.get('/settings/google-sheets/status'),
  rescheduleSync: () => api.post('/settings/google-sheets/reschedule'),
};

// Reports API
export const reportsAPI = {
  getAttendance: (params) => api.get('/reports/attendance', { params }),
  getDaily: (employeeId, params = {}) => api.get('/reports/daily', { params: { employeeId, ...params } }),
  getWeekly: (weekStart, employeeId, params = {}) => api.get('/reports/weekly', { params: { weekStart, employeeId, ...params } }),
  getMonthly: (year, month, employeeId, params = {}) => api.get('/reports/monthly', { params: { year, month, employeeId, ...params } }),
  getDayEnd: (date) => api.get('/reports/day-end', { params: { date } }),
  getCalendar: (month, year) => api.get('/reports/calendar', { params: { month, year } }),
  exportReport: (params) => api.get('/reports/export', { params, responseType: 'blob' }),
};

// Tasks API
export const tasksAPI = {
  getAll: (params) => api.get('/tasks', { params }),
  getById: (id) => api.get(`/tasks/${id}`),
  create: (data) => api.post('/tasks', data),
  update: (id, data) => api.put(`/tasks/${id}`, data),
  delete: (id) => api.delete(`/tasks/${id}`),
  accept: (id) => api.post(`/tasks/${id}/accept`),
  reject: (id, reason) => api.post(`/tasks/${id}/reject`, { reason }),
  complete: (id, notes) => api.post(`/tasks/${id}/complete`, { notes }),
  getStats: () => api.get('/tasks/stats/summary'),
};

// Employee Holidays API
export const employeeHolidaysAPI = {
  getOptional: (params) => api.get('/employee-holidays/optional', { params }),
  select: (data) => api.post('/employee-holidays/select', data),
  getMySelections: (params) => api.get('/employee-holidays/my-selections', { params }),
  getAll: (params) => api.get('/employee-holidays/all', { params }),
  updateStatus: (id, data) => api.put(`/employee-holidays/${id}/status`, data),
  bulkUpdateStatus: (data) => api.put('/employee-holidays/bulk-status', data),
  getEmployeeApproved: (employeeId, params) => api.get(`/employee-holidays/employee/${employeeId}/approved`, { params }),
};

// Companies API
export const companiesAPI = {
  getAll: () => api.get('/companies'),
  getById: (id) => api.get(`/companies/${id}`),
  getActive: () => api.get('/companies/active'),
  create: (data) => {
    let formData;
    if (data instanceof FormData) {
      formData = data;
    } else {
      formData = new FormData();
      Object.keys(data).forEach(key => {
        formData.append(key, data[key] || '');
      });
    }
    return api.post('/companies', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  update: (id, data) => {
    let formData;
    if (data instanceof FormData) {
      formData = data;
    } else {
      formData = new FormData();
      Object.keys(data).forEach(key => {
        formData.append(key, data[key] || '');
      });
    }
    return api.put(`/companies/${id}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  delete: (id) => api.delete(`/companies/${id}`),
};

// Payslips API
export const payslipsAPI = {
  getMyPayslip: (params) => api.get('/payslips', { params }),
  getAll: (params) => api.get('/payslips/all', { params }),
  create: (data) => api.post('/payslips', data),
  updateStatus: (id, status) => api.patch(`/payslips/${id}/status`, { status }),
  delete: (id) => api.delete(`/payslips/${id}`),
  getAttendanceSummary: (employeeId, month, year) => api.get(`/payslips/attendance-summary/${employeeId}/${month}/${year}`),
  getReports: (employeeId, month, year) => api.get(`/payslips/reports/${employeeId}/${month}/${year}`),
  getAttendanceLeaveDetails: (employeeId, month, year) => api.get(`/payslips/attendance-leave-details/${employeeId}/${month}/${year}`),
  generateForMonth: (month, year) => api.post('/payslips/generate-for-month', { month, year }),
};

// Logs API
export const logsAPI = {
  getAll: (params) => api.get('/logs', { params }),
  getSummary: () => api.get('/logs/summary'),
  getStats: (params) => api.get('/logs/stats', { params }),
  clearLogs: (days) => api.delete('/logs/clear', { data: { days } }),
  exportLogs: (params) => api.get('/logs/export', { params, responseType: 'blob' }),
};

// Google Sheets API
export const googleSheetsAPI = {
  exportPayslip: (data) => api.post('/google-sheets/export-payslip', data),
  createSpreadsheet: (data) => api.post('/google-sheets/create-spreadsheet', data),
  getSpreadsheet: (spreadsheetId) => api.get(`/google-sheets/spreadsheet/${spreadsheetId}`),
};

export default api;
